import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { apiFetch } from '../../services/api';
import AdminShell from './AdminShell';

const LLM_PROVIDERS = ['anthropic', 'openai', 'google', 'mistral', 'cohere'];

const JSONB_FIELDS = [
  { key: 'can_do',             label: 'Can Do' },
  { key: 'cannot_do',          label: 'Cannot Do' },
  { key: 'task_types',         label: 'Task Types' },
  { key: 'input_requirements', label: 'Input Requirements' },
  { key: 'output_types',       label: 'Output Types' },
];

function useMarked() {
  const [ready, setReady] = useState(!!window.marked);
  useEffect(() => {
    if (window.marked) return;
    const s = document.createElement('script');
    s.src = 'https://cdnjs.cloudflare.com/ajax/libs/marked/9.1.6/marked.min.js';
    s.onload = () => setReady(true);
    document.head.appendChild(s);
  }, []);
  return ready;
}

function isValidJson(str) {
  try { JSON.parse(str); return true; }
  catch { return false; }
}

export default function AdminAgentDetailPage() {
  const { agentId } = useParams();
  const { user }    = useAuth();
  const navigate    = useNavigate();
  const markedReady = useMarked();

  const [agent,   setAgent]   = useState(null);
  const [loading, setLoading] = useState(true);

  // Edit state
  const [editMode,  setEditMode]  = useState(false);
  const [editForm,  setEditForm]  = useState({});
  const [jsonErrors, setJsonErrors] = useState({}); // field -> error string
  const [saving,    setSaving]    = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [promptTab, setPromptTab] = useState('edit'); // 'edit' | 'preview'

  // Delete state
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (user && !user.isSuperAdmin) navigate('/dashboard', { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    if (!agentId) return;
    setLoading(true);
    apiFetch(`/api/admin/agents/${agentId}`)
      .then(data => setAgent(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [agentId]);

  function openEdit() {
    const form = {
      agent_key:                agent.agent_key                ?? '',
      name:                     agent.name                     ?? '',
      llm_provider_code:        agent.llm_provider_code        ?? '',
      llm_model_code:           agent.llm_model_code           ?? '',
      recommended_input_token:  agent.recommended_input_token  ?? '',
      recommended_output_token: agent.recommended_output_token ?? '',
      short_description:        agent.short_description        ?? '',
      planner_summary:          agent.planner_summary          ?? '',
      execution_prompt_md:      agent.execution_prompt_md      ?? '',
      is_active:                agent.is_active                ?? true,
    };
    // JSONB fields stored as formatted JSON strings in editForm
    for (const { key } of JSONB_FIELDS) {
      form[key] = JSON.stringify(agent[key] ?? [], null, 2);
    }
    setEditForm(form);
    setJsonErrors({});
    setSaveError(null);
    setPromptTab('edit');
    setEditMode(true);
  }

  function cancelEdit() { setEditMode(false); setSaveError(null); }

  function setField(key, value) {
    setEditForm(f => ({ ...f, [key]: value }));
  }

  function validateJsonFields() {
    const errors = {};
    for (const { key, label } of JSONB_FIELDS) {
      if (!isValidJson(editForm[key])) {
        errors[key] = `${label}: invalid JSON`;
      }
    }
    setJsonErrors(errors);
    return Object.keys(errors).length === 0;
  }

  async function handleSave() {
    if (!validateJsonFields()) return;
    setSaving(true);
    setSaveError(null);
    try {
      const payload = { ...editForm };
      // Parse JSONB fields back to arrays before sending
      for (const { key } of JSONB_FIELDS) {
        payload[key] = JSON.parse(editForm[key]);
      }
      const updated = await apiFetch(
        `/api/admin/agents/${agentId}`,
        { method: 'PATCH', body: JSON.stringify(payload) }
      );
      setAgent(updated);
      setEditMode(false);
    } catch (e) {
      setSaveError(e?.message ?? 'Save failed. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    setDeleting(true);
    try {
      await apiFetch(`/api/admin/agents/${agentId}`, { method: 'DELETE' });
      navigate('/admin/agents', { replace: true });
    } catch (e) {
      setSaveError(e?.message ?? 'Delete failed.');
      setConfirmDelete(false);
    } finally {
      setDeleting(false);
    }
  }

  const crumbs = [
    { label: 'Dashboard', href: '/admin' },
    { label: 'Agents', href: '/admin/agents' },
    { label: agent?.name ?? agentId },
  ];

  if (loading) {
    return (
      <AdminShell crumbs={crumbs}>
        <p style={{ color: 'var(--ink-mute)', fontSize: '13px' }}>Loading…</p>
      </AdminShell>
    );
  }

  if (!agent) {
    return (
      <AdminShell crumbs={crumbs}>
        <p style={{ color: 'var(--accent)', fontSize: '13px' }}>Agent not found.</p>
      </AdminShell>
    );
  }

  return (
    <AdminShell crumbs={crumbs}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px',
        gap: '28px', alignItems: 'start' }}>

        {/* ── Main column ── */}
        <div>

          {/* Title + actions */}
          <div style={{ display: 'flex', alignItems: 'flex-start',
            justifyContent: 'space-between', gap: '16px',
            marginBottom: '20px', flexWrap: 'wrap' }}>
            <div>
              <h1 style={{ fontFamily: 'var(--serif)', fontSize: '24px',
                fontWeight: '400', lineHeight: 1.3, marginBottom: '8px' }}>
                {agent.name}
              </h1>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap',
                alignItems: 'center' }}>
                <span style={{ fontFamily: 'monospace', fontSize: '12px',
                  color: 'var(--ink-mute)', background: 'var(--paper-3)',
                  padding: '2px 8px', borderRadius: '4px' }}>
                  {agent.agent_key}
                </span>
                <span style={{
                  fontSize: '11px', fontWeight: '600', padding: '3px 10px',
                  borderRadius: '100px',
                  background: agent.is_active
                    ? 'rgba(45,138,110,0.12)' : 'rgba(180,180,180,0.15)',
                  color: agent.is_active ? '#2d8a6e' : 'var(--ink-mute)',
                }}>
                  {agent.is_active ? '● Active' : '○ Inactive'}
                </span>
                {agent.llm_provider_code && (
                  <span style={{ fontSize: '12px', color: 'var(--ink-mute)' }}>
                    {agent.llm_provider_code} / {agent.llm_model_code}
                  </span>
                )}
              </div>
            </div>

            {!editMode && (
              <div style={{ display: 'flex', gap: '8px' }}>
                <button onClick={openEdit} style={secondaryBtn}>✏ Edit</button>
                <button
                  onClick={() => setConfirmDelete(true)}
                  style={{ ...secondaryBtn, color: 'var(--accent)',
                    borderColor: 'rgba(200,86,58,0.3)' }}>
                  Delete
                </button>
              </div>
            )}
          </div>

          {/* Delete confirmation banner */}
          {confirmDelete && (
            <div style={{
              background: 'rgba(200,86,58,0.08)',
              border: '1px solid rgba(200,86,58,0.3)',
              borderRadius: 'var(--r)', padding: '14px 16px',
              marginBottom: '16px', display: 'flex',
              alignItems: 'center', gap: '12px', flexWrap: 'wrap',
            }}>
              <span style={{ fontSize: '13px', color: '#a03020', flex: 1 }}>
                ⚠ Permanently delete <strong>{agent.name}</strong>? This cannot be undone.
              </span>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  style={{ ...primaryBtn, background: 'var(--accent)',
                    opacity: deleting ? 0.6 : 1 }}>
                  {deleting ? 'Deleting…' : 'Yes, delete'}
                </button>
                <button onClick={() => setConfirmDelete(false)} style={secondaryBtn}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* ── Edit form ── */}
          {editMode ? (
            <div style={{
              background: 'var(--white)', border: '1px solid var(--paper-3)',
              borderRadius: 'var(--r)', padding: '20px 24px',
            }}>
              {/* Row 1: name + key */}
              <div style={{ display: 'flex', gap: '12px', marginBottom: '16px',
                flexWrap: 'wrap' }}>
                <div style={{ flex: 2, minWidth: '180px' }}>
                  <label style={labelStyle}>Name</label>
                  <input value={editForm.name}
                    onChange={e => setField('name', e.target.value)}
                    style={inputStyle} />
                </div>
                <div style={{ flex: 1, minWidth: '140px' }}>
                  <label style={labelStyle}>Agent Key</label>
                  <input value={editForm.agent_key}
                    onChange={e => setField('agent_key', e.target.value)}
                    style={{ ...inputStyle, fontFamily: 'monospace' }} />
                </div>
              </div>

              {/* Row 2: provider + model + tokens */}
              <div style={{ display: 'flex', gap: '12px', marginBottom: '16px',
                flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: '130px' }}>
                  <label style={labelStyle}>LLM Provider</label>
                  <select value={editForm.llm_provider_code}
                    onChange={e => setField('llm_provider_code', e.target.value)}
                    style={inputStyle}>
                    <option value="">— none —</option>
                    {LLM_PROVIDERS.map(p => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                </div>
                <div style={{ flex: 2, minWidth: '180px' }}>
                  <label style={labelStyle}>Model Code</label>
                  <input value={editForm.llm_model_code}
                    onChange={e => setField('llm_model_code', e.target.value)}
                    style={{ ...inputStyle, fontFamily: 'monospace' }} />
                </div>
                <div style={{ minWidth: '100px' }}>
                  <label style={labelStyle}>Input Tokens</label>
                  <input value={editForm.recommended_input_token}
                    onChange={e => setField('recommended_input_token', e.target.value)}
                    style={inputStyle} placeholder="e.g. 4096" />
                </div>
                <div style={{ minWidth: '100px' }}>
                  <label style={labelStyle}>Output Tokens</label>
                  <input value={editForm.recommended_output_token}
                    onChange={e => setField('recommended_output_token', e.target.value)}
                    style={inputStyle} placeholder="e.g. 2048" />
                </div>
              </div>

              {/* Short description */}
              <div style={{ marginBottom: '16px' }}>
                <label style={labelStyle}>Short Description</label>
                <input value={editForm.short_description}
                  onChange={e => setField('short_description', e.target.value)}
                  style={inputStyle} />
              </div>

              {/* Planner summary */}
              <div style={{ marginBottom: '16px' }}>
                <label style={labelStyle}>Planner Summary</label>
                <textarea value={editForm.planner_summary}
                  onChange={e => setField('planner_summary', e.target.value)}
                  rows={3}
                  style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.5 }} />
              </div>

              {/* JSONB fields — 2 col grid */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr',
                gap: '12px', marginBottom: '16px' }}>
                {JSONB_FIELDS.map(({ key, label }) => (
                  <div key={key}>
                    <label style={labelStyle}>{label}</label>
                    <textarea
                      value={editForm[key]}
                      onChange={e => {
                        setField(key, e.target.value);
                        if (jsonErrors[key]) {
                          setJsonErrors(prev => {
                            const next = { ...prev }; delete next[key]; return next;
                          });
                        }
                      }}
                      rows={5}
                      style={{
                        ...inputStyle, resize: 'vertical',
                        fontFamily: 'monospace', fontSize: '12px',
                        lineHeight: 1.5,
                        borderColor: jsonErrors[key]
                          ? 'rgba(200,86,58,0.6)' : undefined,
                      }}
                    />
                    {jsonErrors[key] && (
                      <p style={{ fontSize: '11px', color: 'var(--accent)',
                        marginTop: '3px' }}>
                        {jsonErrors[key]}
                      </p>
                    )}
                  </div>
                ))}
              </div>

              {/* Execution prompt — markdown editor with tabs */}
              <div style={{ marginBottom: '16px' }}>
                <label style={labelStyle}>Execution Prompt (Markdown)</label>
                <div style={{ display: 'flex', gap: '4px', marginBottom: '8px' }}>
                  {['edit', 'preview'].map(tab => (
                    <button key={tab}
                      onClick={() => setPromptTab(tab)}
                      style={{
                        padding: '4px 12px', fontSize: '12px', borderRadius: '6px',
                        border: '1px solid var(--paper-3)', cursor: 'pointer',
                        fontFamily: 'var(--sans)',
                        background: promptTab === tab ? 'var(--ink)' : 'var(--white)',
                        color:      promptTab === tab ? 'var(--paper)' : 'var(--ink-soft)',
                        textTransform: 'capitalize',
                      }}>
                      {tab}
                    </button>
                  ))}
                </div>
                {promptTab === 'preview' ? (
                  <div
                    style={{
                      minHeight: '280px', padding: '16px',
                      background: 'var(--paper-2)', borderRadius: '6px',
                      border: '1px solid var(--paper-3)',
                      fontSize: '13px', lineHeight: 1.7,
                      color: 'var(--ink)', overflowY: 'auto',
                    }}
                    dangerouslySetInnerHTML={{
                      __html: markedReady && window.marked
                        ? window.marked.parse(editForm.execution_prompt_md || '',
                            { gfm: true, breaks: true })
                        : `<pre style="white-space:pre-wrap">${editForm.execution_prompt_md}</pre>`
                    }}
                  />
                ) : (
                  <textarea
                    value={editForm.execution_prompt_md}
                    onChange={e => setField('execution_prompt_md', e.target.value)}
                    rows={20}
                    style={{
                      ...inputStyle, resize: 'vertical',
                      fontFamily: 'monospace', fontSize: '12px',
                      lineHeight: 1.6, minHeight: '280px',
                    }}
                  />
                )}
              </div>

              {/* Active toggle */}
              <div style={{ marginBottom: '20px', display: 'flex',
                alignItems: 'center', gap: '10px' }}>
                <input type="checkbox" id="is_active"
                  checked={editForm.is_active}
                  onChange={e => setField('is_active', e.target.checked)}
                  style={{ width: '14px', height: '14px', cursor: 'pointer' }} />
                <label htmlFor="is_active"
                  style={{ fontSize: '13px', cursor: 'pointer',
                    color: 'var(--ink-soft)' }}>
                  Active
                </label>
              </div>

              {saveError && (
                <p style={{ marginBottom: '12px', fontSize: '12px',
                  color: 'var(--accent)', background: 'rgba(200,86,58,0.08)',
                  padding: '8px 12px', borderRadius: '6px' }}>
                  {saveError}
                </p>
              )}

              <div style={{ display: 'flex', gap: '8px' }}>
                <button onClick={handleSave} disabled={saving}
                  style={{ ...primaryBtn, opacity: saving ? 0.6 : 1 }}>
                  {saving ? 'Saving…' : '✓ Save changes'}
                </button>
                <button onClick={cancelEdit} style={secondaryBtn}>Cancel</button>
              </div>
            </div>

          ) : (
            /* ── Read-only view ── */
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

              {/* Short description + planner summary */}
              {(agent.short_description || agent.planner_summary) && (
                <Section title="Overview">
                  {agent.short_description && (
                    <p style={{ fontSize: '14px', lineHeight: 1.6,
                      color: 'var(--ink)', marginBottom: agent.planner_summary ? '12px' : 0 }}>
                      {agent.short_description}
                    </p>
                  )}
                  {agent.planner_summary && (
                    <p style={{ fontSize: '13px', lineHeight: 1.6,
                      color: 'var(--ink-soft)' }}>
                      {agent.planner_summary}
                    </p>
                  )}
                </Section>
              )}

              {/* JSONB fields */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr',
                gap: '16px' }}>
                {JSONB_FIELDS.map(({ key, label }) => {
                  const items = agent[key] ?? [];
                  return (
                    <Section key={key} title={label}>
                      {items.length === 0 ? (
                        <p style={{ fontSize: '12px', color: 'var(--ink-mute)' }}>—</p>
                      ) : (
                        <ul style={{ margin: 0, paddingLeft: '16px' }}>
                          {items.map((item, i) => (
                            <li key={i} style={{ fontSize: '13px', lineHeight: 1.6,
                              color: 'var(--ink-soft)', marginBottom: '2px' }}>
                              {typeof item === 'string' ? item : JSON.stringify(item)}
                            </li>
                          ))}
                        </ul>
                      )}
                    </Section>
                  );
                })}
              </div>

              {/* Execution prompt */}
              <Section title="Execution Prompt">
                {agent.execution_prompt_md ? (
                  markedReady ? (
                    <div
                      style={{ fontSize: '13px', lineHeight: 1.7, color: 'var(--ink)' }}
                      dangerouslySetInnerHTML={{
                        __html: window.marked.parse(
                          agent.execution_prompt_md, { gfm: true, breaks: true }
                        )
                      }}
                    />
                  ) : (
                    <pre style={{ fontFamily: 'monospace', fontSize: '12px',
                      lineHeight: 1.6, whiteSpace: 'pre-wrap',
                      color: 'var(--ink-soft)' }}>
                      {agent.execution_prompt_md}
                    </pre>
                  )
                ) : (
                  <p style={{ fontSize: '13px', color: 'var(--ink-mute)' }}>
                    No prompt defined.
                  </p>
                )}
              </Section>
            </div>
          )}
        </div>

        {/* ── Sidebar ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

          <SideSection title="Details">
            {[
              ['Agent ID',  agent.agent_id],
              ['Created',   new Date(agent.created_at).toLocaleString()],
              ['Updated',   new Date(agent.updated_at).toLocaleString()],
            ].map(([label, value]) => (
              <div key={label} style={{ display: 'flex',
                justifyContent: 'space-between', gap: '8px', marginBottom: '6px' }}>
                <span style={{ fontSize: '11px', color: 'var(--ink-mute)',
                  flexShrink: 0 }}>{label}</span>
                <span style={{ fontSize: '12px', fontWeight: '500',
                  textAlign: 'right', wordBreak: 'break-all' }}>{value}</span>
              </div>
            ))}
            {agent.llm_provider_code && (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between',
                  gap: '8px', marginBottom: '6px' }}>
                  <span style={{ fontSize: '11px', color: 'var(--ink-mute)' }}>Provider</span>
                  <span style={{ fontSize: '12px', fontWeight: '500' }}>
                    {agent.llm_provider_code}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between',
                  gap: '8px', marginBottom: '6px' }}>
                  <span style={{ fontSize: '11px', color: 'var(--ink-mute)' }}>Model</span>
                  <span style={{ fontSize: '12px', fontFamily: 'monospace',
                    textAlign: 'right', wordBreak: 'break-all' }}>
                    {agent.llm_model_code}
                  </span>
                </div>
              </>
            )}
            {(agent.recommended_input_token || agent.recommended_output_token) && (
              <div style={{ display: 'flex', justifyContent: 'space-between',
                gap: '8px', marginBottom: '6px' }}>
                <span style={{ fontSize: '11px', color: 'var(--ink-mute)' }}>Tokens</span>
                <span style={{ fontSize: '12px', color: 'var(--ink-soft)' }}>
                  in {agent.recommended_input_token ?? '—'} /
                  out {agent.recommended_output_token ?? '—'}
                </span>
              </div>
            )}
          </SideSection>

          <SideSection title="Quick actions">
            <button
              onClick={openEdit}
              style={{ ...secondaryBtn, width: '100%', marginBottom: '8px',
                justifyContent: 'center', display: 'flex' }}>
              ✏ Edit agent
            </button>
            <Link
              to="/admin/agents/new"
              style={{ ...secondaryBtn, width: '100%', textDecoration: 'none',
                display: 'flex', justifyContent: 'center',
                boxSizing: 'border-box' }}>
              + New agent
            </Link>
          </SideSection>
        </div>
      </div>
    </AdminShell>
  );
}

function Section({ title, children }) {
  return (
    <div style={{
      background: 'var(--white)', border: '1px solid var(--paper-3)',
      borderRadius: 'var(--r)', padding: '20px 24px',
    }}>
      <div style={{
        fontSize: '10px', fontWeight: '600', letterSpacing: '0.1em',
        textTransform: 'uppercase', color: 'var(--ink-mute)',
        marginBottom: '12px', paddingBottom: '8px',
        borderBottom: '1px solid var(--paper-3)',
      }}>
        {title}
      </div>
      {children}
    </div>
  );
}

function SideSection({ title, children }) {
  return (
    <div style={{
      background: 'var(--white)', border: '1px solid var(--paper-3)',
      borderRadius: 'var(--r)', padding: '16px 18px',
    }}>
      <div style={{
        fontSize: '10px', fontWeight: '600', letterSpacing: '0.1em',
        textTransform: 'uppercase', color: 'var(--ink-mute)',
        marginBottom: '12px', paddingBottom: '8px',
        borderBottom: '1px solid var(--paper-3)',
      }}>
        {title}
      </div>
      {children}
    </div>
  );
}

const labelStyle = {
  display: 'block', fontSize: '12px', fontWeight: '500',
  color: 'var(--ink-mute)', marginBottom: '6px', letterSpacing: '0.03em',
};

const inputStyle = {
  width: '100%', padding: '8px 12px', fontSize: '13px',
  border: '1px solid var(--paper-3)', borderRadius: '6px',
  fontFamily: 'var(--sans)', background: 'var(--white)',
  color: 'var(--ink)', boxSizing: 'border-box',
};

const primaryBtn = {
  padding: '9px 18px', background: 'var(--accent)', color: '#fff',
  border: 'none', borderRadius: '6px', fontFamily: 'var(--sans)',
  fontSize: '13px', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap',
};

const secondaryBtn = {
  padding: '9px 18px', background: 'var(--white)', color: 'var(--ink)',
  border: '1px solid var(--paper-3)', borderRadius: '6px',
  fontFamily: 'var(--sans)', fontSize: '13px', fontWeight: '500',
  cursor: 'pointer', whiteSpace: 'nowrap',
};
